import { Component, ErrorInfo, forwardRef, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback: ReactNode | ((error?: Error, info?: ErrorInfo) => ReactNode);
  errorHandler?: (error: Error, info: ErrorInfo) => boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  info?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error, info: ErrorInfo) {
    return { hasError: true, error, info };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // If error handler exists and returns true, skip console.error.
    if (!this.props.errorHandler || !this.props.errorHandler(error, info)) {
      console.error(error, info);
    } else {
      console.warn(error, info);
    }
  }

  render() {
    if (this.state.hasError) {
      if (typeof this.props.fallback === 'function') {
        return this.props.fallback(this.state.error, this.state.info);
      }
      return this.props.fallback;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;

export const withSelfRecoveryErrorBoundary = (
  Component,
  errorHandler?: (error: Error, info: ErrorInfo) => boolean
) => {
  const ComponentWithBoundary = (props: object, ref) => (
    <ErrorBoundary
      fallback={() => <Component {...props} ref={ref} />}
      errorHandler={errorHandler}
    >
      <Component {...props} ref={ref} />
    </ErrorBoundary>
  );
  return forwardRef(ComponentWithBoundary);
};

export const withErrorBoundary = (
  Component,
  errorHandler?: (error: Error, info: ErrorInfo) => boolean
) => {
  const ComponentWithBoundary = (props: object, ref) => (
    <ErrorBoundary fallback={() => <></>} errorHandler={errorHandler}>
      <Component {...props} ref={ref} />
    </ErrorBoundary>
  );
  return forwardRef(ComponentWithBoundary);
};
